import { v4 as uuidv4 } from 'uuid';
import {
  upc12ToGTIN14,
  validateUPC12,
} from 'common/components/product-add/components/utils/validation';

/**
 * If `caseGtin` is empty and `caseUpc12` is a valid 12-digit value with a
 * correct check digit, returns the product with `caseGtin` auto-filled.
 * Otherwise returns the product unchanged.
 */
export const autoPopulateCaseGtin = (product) => {
  if (product.caseGtin?.trim()) return product;
  const gtin = validateUPC12(product.caseUpc12).isValid
    ? upc12ToGTIN14(product.caseUpc12)
    : null;
  return gtin ? { ...product, caseGtin: gtin } : product;
};

export const getQuestionParams = (state) => {
  const { answers, arrivalDate } = state;

  return {
    productCount: answers.productCount,
    foodServiceBulkItems: answers.foodServiceBulkItems ?? null,
    variableWeight: answers.variableWeight ?? null,
    alcoholicBeverages: answers.alcoholicBeverages ?? null,
    sellableInnerPack: answers.sellableInnerPack ?? null,
    beer: answers.beer ?? null,
    wine: answers.wine ?? null,
    spirits: answers.spirits ?? null,
    displayShippers: answers.displayShippers ?? null,
    shelfDisplayBoxTray: answers.shelfDisplayBoxTray ?? null,
    perishable: answers.perishable ?? null,
    arrivalDate: arrivalDate ?? null,
    multipacks: answers.multipacks ?? null,
  };
};

/**
 * Maps a single `wizardFormItem` object (from the BE response) onto the
 * internal wizard product shape.
 *
 * Kept as a pure helper so it can be reused by `mapWizardFormItemsToProducts`
 * and `updateProductFromHierarchy` without duplication.
 *
 * @param {object} wizardFormItem - The `wizardFormItem` sub-object from the API.
 * @returns {object} Partial wizard product with all BE fields normalised.
 */
export const mapFields = (wizardFormItem) => {
  return {
    // ── Identity ─────────────────────────────────────────────────────────────
    productUnitId: wizardFormItem.productUnitId ?? null,
    productInnerPackId: wizardFormItem.productInnerPackId ?? null,
    productCaseId: wizardFormItem.productCaseId ?? null,

    // ── Core fields ───────────────────────────────────────────────────────────
    unitUpc: wizardFormItem.unitUpc || '',
    vendorItemNumber: wizardFormItem.vendorItemNumber || '',
    brandName: wizardFormItem.brandName || '',
    brandId: wizardFormItem.brandId || '',
    productName: wizardFormItem.productName || '',
    productDescription: wizardFormItem.productDescription || '',
    ecommerceDescription: wizardFormItem.ecommerceDescription ?? '',
    size: wizardFormItem.size ?? null,
    sizeUom: wizardFormItem.sizeUom || '',

    // ── Inner pack ────────────────────────────────────────────────────────────
    innerPackUpc: wizardFormItem.innerPackUpc || '',
    innerPackGtin: wizardFormItem.innerPackGtin || '',
    innerPackPackSize: wizardFormItem.innerPackPackSize ?? null,
    innerPackSellable: wizardFormItem.innerPackSellable ?? null,

    // ── Case ──────────────────────────────────────────────────────────────────
    caseUpc12: wizardFormItem.caseUpc12 || '',
    caseGtin: wizardFormItem.caseGtin || '',
    caseInnerPackCount: wizardFormItem.caseInnerPackCount ?? null,
    casePackSize: wizardFormItem.casePackSize ?? null,
    caseSellable: wizardFormItem.caseSellable ?? null,
    noCodeCase: wizardFormItem.noCodeCase ?? null,

    // ── Conditional flags ─────────────────────────────────────────────────────
    merchandisedInTray: wizardFormItem.merchandisedInTray ?? null,
    perishable: wizardFormItem.perishable ?? null,
    foodServiceBulkItems: wizardFormItem.foodServiceBulkItems ?? null,
    variableWeight: wizardFormItem.variableWeight ?? null,

    // ── Beer ──────────────────────────────────────────────────────────────────
    beerRegion: wizardFormItem.beerRegion ?? null,
    beerCategory: wizardFormItem.beerCategory ?? null,
    ibus: wizardFormItem.ibus ?? null,
    beerStyle: wizardFormItem.beerStyle ?? null,
    beerTaste: wizardFormItem.beerTaste ?? null,
    beerBody: wizardFormItem.beerBody ?? null,
    beerFinalGravity: wizardFormItem.beerFinalGravity ?? null,

    // ── Wine ──────────────────────────────────────────────────────────────────
    wineCategory: wizardFormItem.wineCategory ?? null,
    wineVarietal: wizardFormItem.wineVarietal ?? null,
    wineSweetness: wizardFormItem.wineSweetness ?? null,
    wineOrigin: wizardFormItem.wineOrigin ?? null,
    wineRegion: wizardFormItem.wineRegion ?? null,
    wineCorkType: wizardFormItem.wineCorkType ?? null,
    winePairsWellWith: wizardFormItem.winePairsWellWith ?? null,
    wineComplements: wizardFormItem.wineComplements ?? null,
    wineVintage: wizardFormItem.wineVintage ?? null,
    wineTaste: wizardFormItem.wineTaste ?? null,
    wineBody: wizardFormItem.wineBody ?? null,
    wineCharacter: wizardFormItem.wineCharacter ?? null,

    // ── Spirits ───────────────────────────────────────────────────────────────
    spiritsFlavor: wizardFormItem.spiritsFlavor ?? null,
    spiritsCategory: wizardFormItem.spiritsCategory ?? null,
    spiritsSubCategory: wizardFormItem.spiritsSubCategory ?? null,
    spiritsStyle: wizardFormItem.spiritsStyle ?? null,
    spiritsRegion: wizardFormItem.spiritsRegion ?? null,
    spiritsTaste: wizardFormItem.spiritsTaste ?? null,

    // ── Shipper ───────────────────────────────────────────────────────────────
    displayShipper: wizardFormItem.displayShipper ?? null,
    displayShippersDescription:
      wizardFormItem.displayShippersDescription ||
      wizardFormItem.productDescription ||
      '',
    displayShippersTotalUnit: wizardFormItem.displayShippersTotalUnit ?? null,
    displayShipperItems: filterDisplayShipperItems(
      wizardFormItem.displayShipperItems
    ),

    // ── Multipack ─────────────────────────────────────────────────────────────
    multipack: wizardFormItem.multipack ?? null,
    multipackDescription:
      wizardFormItem.multipackDescription ||
      wizardFormItem.productDescription ||
      '',
    multipackTotalUnit: wizardFormItem.multipackTotalUnit ?? null,
    multipackItems: filterMultipackItems(wizardFormItem.multipackItems),
  };
};

/**
 * Maps the `wizardFormItems` array from `wizard/get-wizard-form-items` into
 * the internal wizard product shape used by the grid and reducer.
 *
 * Key contract:
 * - Reads from `item.wizardFormItem` (BE nests the data one level deep).
 * - Carries `item.hierarchies` onto the product so `WizardHierarchyAction`
 *   can open the hierarchy picker without a separate API call.
 * - Generates a fresh `uniqueId` for every row.
 *
 * @param {Array<{ wizardFormItem: object, hierarchies: Array }>} items
 * @returns {Array} Array of wizard product objects ready for grid / reducer.
 */

export const mapHierarchy = (h) => ({
  productHierarchyId: h.hierarchyId,
  gtin: h.gtin,
  packDescription: h.packDescription,
  productItemLinks: h.products ?? [],
});

export const mapWizardFormItemsToProducts = (items = []) => {
  return items.map((item) =>
    autoPopulateCaseGtin({
      uniqueId: uuidv4(),
      ...mapFields(item.wizardFormItem),
      hierarchyId: item.wizardFormItem.hierarchyId,
      hierarchies: (item.hierarchies ?? []).map(mapHierarchy),
    })
  );
};

/**
 * Creates an empty product with all BE schema fields initialized to their
 * zero/null defaults. Used when the user clicks "New Product" in the grid.
 */
export const createEmptyWizardProduct = (answers = {}) => {
  const isDisplayShipper = Boolean(answers.displayShippers);

  return {
    // ── Identity ─────────────────────────────────────────────────────────────
    uniqueId: uuidv4(),
    productUnitId: null,
    productInnerPackId: null,
    productCaseId: null,

    // ── Core fields ───────────────────────────────────────────────────────────
    unitUpc: '',
    vendorItemNumber: '',
    brandName: '',
    brandId: null,
    productName: '',
    productDescription: '',
    ecommerceDescription: '',
    size: null,
    sizeUom: '',

    // ── Innerpack ─────────────────────────────────────────────────────────────
    innerPackUpc: '',
    innerPackGtin: '',
    innerPackPackSize: null,
    innerPackSellable: answers.sellableInnerPack ? true : null,

    // ── Case ──────────────────────────────────────────────────────────────────
    caseGtin: '',
    caseUpc12: '',
    caseInnerPackCount: null,
    casePackSize: null,
    caseSellable: null,
    noCodeCase: null,

    // ── Conditional flags ─────────────────────────────────────────────────────
    merchandisedInTray: isDisplayShipper
      ? false
      : answers.shelfDisplayBoxTray
        ? true
        : null,
    perishable: answers.perishable ? true : null,
    foodServiceBulkItems: answers.foodServiceBulkItems ? true : null,
    variableWeight: answers.variableWeight ? true : null,

    // ── Beer fields ───────────────────────────────────────────────────────────
    beerRegion: null,
    beerCategory: null,
    ibus: null,
    beerStyle: null,
    beerTaste: null,
    beerBody: null,
    beerFinalGravity: null,

    // ── Wine fields ───────────────────────────────────────────────────────────
    wineCategory: null,
    wineVarietal: null,
    wineSweetness: null,
    wineOrigin: null,
    wineRegion: null,
    wineCorkType: null,
    winePairsWellWith: null,
    wineComplements: null,
    wineVintage: null,
    wineTaste: null,
    wineBody: null,
    wineCharacter: null,

    // ── Spirits fields ────────────────────────────────────────────────────────
    spiritsFlavor: null,
    spiritsCategory: null,
    spiritsSubCategory: null,
    spiritsStyle: null,
    spiritsRegion: null,
    spiritsTaste: null,

    // ── Shipper fields ────────────────────────────────────────────────────────
    displayShippersDescription: '',
    displayShippersTotalUnit: null,
    displayShipperItems: [],
    displayShipper:
      answers.displayShippers ?? (answers.displayShippers ? true : false),

    // ── Multipack fields ──────────────────────────────────────────────────────
    multipackDescription: '',
    multipackTotalUnit: null,
    multipackItems: [],
    multipack: answers.multipacks ?? (answers.multipacks ? true : false),
  };
};

export const ANSWER_KEYS = [
  'alcoholicBeverages',
  'shelfDisplayBoxTray',
  'perishable',
  'foodServiceBulkItems',
  'variableWeight',
  'beer',
  'wine',
  'spirits',
  'sellableInnerPack',
  'displayShippers',
  'multipacks',
];

/**
 * Derives answer flags from a server response object.
 * Only keys that are actually present in `data` are included in the result —
 * missing keys are omitted so they cannot overwrite existing answer state
 * when the result is spread/merged.
 */
export const deriveAnswersFromResponse = (data) => {
  if (!data) return {};
  return Object.fromEntries(
    ANSWER_KEYS.filter((key) => key in data).map((key) => [
      key,
      data[key] || false,
    ])
  );
};

export const mappingHierarchiesProductWizard = (hierarchies = []) => {
  if (!hierarchies?.length) return [];

  return hierarchies.flatMap((item) => {
    return item.hierarchyItems.map((hierarchyItem) => ({
      fee: null,
      productHierarchyId: hierarchyItem.hierarchyId,
      gtin: hierarchyItem.gtin,
      packDescription: hierarchyItem.packDescription,
      productItemLinks: hierarchyItem.products.map((product) => ({
        productItemId: product.productItemId,
        parentProductItemId: product.parentProductItemId,
        packageLevel: product.packageLevel,
        upc12: product.upc12,
        gtin: product.gtin,
      })),
      wizardFormItem: hierarchyItem.wizardFormItem,
    }));
  });
};

export const filterDisplayShipperItems = (shipperItems = []) => {
  return shipperItems.filter((item) => !!item.itemUpc);
};

export const filterMultipackItems = (multipackItems = []) => {
  return multipackItems.filter((item) => !!item.itemUpc);
};

/**
 * Applies wizard answers as defaults to conditional flag fields on products.
 * Only fills fields that are still null — never overwrites user-entered values.
 */
export const applyAnswersToProducts = (products = [], answers = {}) => {
  return products.map((product) => ({
    ...product,
    merchandisedInTray:
      product.merchandisedInTray ?? (answers.shelfDisplayBoxTray ? true : null),

    perishable: product.perishable ?? (answers.perishable ? true : null),

    foodServiceBulkItems:
      product.foodServiceBulkItems ??
      (answers.foodServiceBulkItems ? true : null),

    variableWeight:
      product.variableWeight ??
      (answers.variableWeight ? true : null),

    innerPackSellable:
      product.innerPackSellable ?? (answers.sellableInnerPack ? true : null),

    displayShipper:
      product.displayShipper ?? (answers.displayShippers ? true : null),

    multipack: product.multipack ?? (answers.multipacks ? true : null),
  }));
};

/**
 * Resets fields that are visually disabled for a given product to their
 * null/empty values before validation or save, so stale values from
 * previously enabled states don't cause false errors.
 *
 * Rules:
 * - Display Shipper: unitUpc, inner pack fields, noCodeCase, merchandisedInTray → cleared
 * - Normal, noCodeCase = true: caseGtin + caseUpc12 → cleared
 * - Food Service: caseSellable → null; innerPackUpc, innerPackGtin, innerPackPackSize → cleared
 * - Innerpack Retailer Sellable (non-Food Service): innerPackGtin → cleared
 */
export const sanitizeProduct = (product) => {
  const isDisplayShipper = Boolean(product.displayShipper);
  const isFoodService = Boolean(product.foodServiceBulkItems);
  const isVariableWeight = Boolean(product.variableWeight);
  const isNoCodeCase = Boolean(product.noCodeCase) && !isVariableWeight;

  let sanitized = { ...product };

  if (isDisplayShipper) {
    sanitized = {
      ...sanitized,
      unitUpc: '',
      innerPackUpc: '',
      innerPackGtin: '',
      noCodeCase: null,
      merchandisedInTray: false,
    };
  } else {
    if (isNoCodeCase) {
      sanitized = { ...sanitized, caseGtin: '', caseUpc12: '' };
    }
  }

  if (isFoodService) {
    sanitized = {
      ...sanitized,
      caseSellable: null,
      innerPackUpc: '',
      innerPackGtin: '',
      innerPackPackSize: null,
    };
  }

  if (isVariableWeight) {
    sanitized = {
      ...sanitized,
      unitUpc: '',
      noCodeCase: false,
      innerPackUpc: '',
      innerPackGtin: '',
      innerPackPackSize: null,
      innerPackSellable: false,
      caseInnerPackCount: null,
      productInnerPackId: null,
    };
  }

  if (sanitized.innerPackSellable && !isFoodService) {
    sanitized = { ...sanitized, innerPackGtin: '' };
  }

  return sanitized;
};

export const updateProductFromHierarchy = ({
  products,
  selectedHierarchy,
  rowData,
}) => {
  if (!selectedHierarchy) return products;

  const { wizardFormItem } = selectedHierarchy;

  if (!wizardFormItem) return products;

  return products.map((product) => {
    const isMatch = product.uniqueId === rowData?.uniqueId;

    if (!isMatch) return product;

    return {
      ...product,

      // Hierarchy reference
      productHierarchyId: selectedHierarchy?.productHierarchyId,

      // Metadata IDs
      productUnitId: wizardFormItem.productUnitId ?? product.productUnitId,
      productCaseId: wizardFormItem.productCaseId ?? product.productCaseId,
      productInnerPackId:
        wizardFormItem.productInnerPackId ?? product.productInnerPackId,

      // Basic info
      brandName: wizardFormItem.brandName || product.brandName,
      brandId: wizardFormItem.brandId || product.brandId,
      productName: wizardFormItem.productName || product.productName,
      productDescription:
        wizardFormItem.productDescription || product.productDescription,
      ecommerceDescription:
        wizardFormItem.ecommerceDescription ?? product.ecommerceDescription,
      size: wizardFormItem.size ?? product.size,
      sizeUom: wizardFormItem.sizeUom || product.sizeUom,
      vendorItemNumber:
        wizardFormItem.vendorItemNumber ?? product.vendorItemNumber,

      // Unit info
      unitUpc: wizardFormItem.unitUpc || product.unitUpc,
      unitGtin: wizardFormItem.unitGtin ?? product.unitGtin,

      // Case info
      caseGtin: wizardFormItem.caseGtin ?? product.caseGtin,
      casePackSize: wizardFormItem.casePackSize ?? product.casePackSize,
      caseInnerPackCount:
        wizardFormItem.caseInnerPackCount ?? product.caseInnerPackCount,
      caseSellable: wizardFormItem.caseSellable ?? product.caseSellable,
      noCodeCase: wizardFormItem.noCodeCase ?? product.noCodeCase,

      // Inner pack info
      innerPackUpc: wizardFormItem.innerPackUpc ?? product.innerPackUpc,
      innerPackGtin: wizardFormItem.innerPackGtin ?? product.innerPackGtin,
      innerPackPackSize:
        wizardFormItem.innerPackPackSize ?? product.innerPackPackSize,
      innerPackSellable:
        wizardFormItem.innerPackSellable ?? product.innerPackSellable,

      // Flags
      merchandisedInTray:
        wizardFormItem.merchandisedInTray ?? product.merchandisedInTray,
      perishable: wizardFormItem.perishable ?? product.perishable,
      foodServiceBulkItems:
        wizardFormItem.foodServiceBulkItems ?? product.foodServiceBulkItems,
      variableWeight:
        wizardFormItem.variableWeight ??
        product.variableWeight,

      // Beverage — beer
      beerRegion: wizardFormItem.beerRegion ?? product.beerRegion,
      beerCategory: wizardFormItem.beerCategory ?? product.beerCategory,
      ibus: wizardFormItem.ibus ?? product.ibus,
      beerStyle: wizardFormItem.beerStyle ?? product.beerStyle,
      beerTaste: wizardFormItem.beerTaste ?? product.beerTaste,
      beerBody: wizardFormItem.beerBody ?? product.beerBody,
      beerFinalGravity:
        wizardFormItem.beerFinalGravity ?? product.beerFinalGravity,

      // Beverage — wine
      wineCategory: wizardFormItem.wineCategory ?? product.wineCategory,
      wineVarietal: wizardFormItem.wineVarietal ?? product.wineVarietal,
      wineSweetness: wizardFormItem.wineSweetness ?? product.wineSweetness,
      wineOrigin: wizardFormItem.wineOrigin ?? product.wineOrigin,
      wineRegion: wizardFormItem.wineRegion ?? product.wineRegion,
      wineCorkType: wizardFormItem.wineCorkType ?? product.wineCorkType,
      winePairsWellWith:
        wizardFormItem.winePairsWellWith ?? product.winePairsWellWith,
      wineComplements:
        wizardFormItem.wineComplements ?? product.wineComplements,
      wineVintage: wizardFormItem.wineVintage ?? product.wineVintage,
      wineTaste: wizardFormItem.wineTaste ?? product.wineTaste,
      wineBody: wizardFormItem.wineBody ?? product.wineBody,
      wineCharacter: wizardFormItem.wineCharacter ?? product.wineCharacter,

      // Beverage — spirits
      spiritsFlavor: wizardFormItem.spiritsFlavor ?? product.spiritsFlavor,
      spiritsCategory:
        wizardFormItem.spiritsCategory ?? product.spiritsCategory,
      spiritsSubCategory:
        wizardFormItem.spiritsSubCategory ?? product.spiritsSubCategory,
      spiritsStyle: wizardFormItem.spiritsStyle ?? product.spiritsStyle,
      spiritsRegion: wizardFormItem.spiritsRegion ?? product.spiritsRegion,
      spiritsTaste: wizardFormItem.spiritsTaste ?? product.spiritsTaste,

      // Shipper
      displayShippersDescription:
        wizardFormItem.displayShippersDescription ??
        product.displayShippersDescription,
      displayShippersTotalUnit:
        wizardFormItem.displayShippersTotalUnit ??
        product.displayShippersTotalUnit,
      displayShipperItems:
        wizardFormItem.displayShipperItems ?? product.displayShipperItems,
    };
  });
};

export const transformWizardItems = (products) => {
  return products.map((product) => ({
    ...product,
    uniqueId: product.uniqueId || uuidv4(),
    displayShipperItems: filterDisplayShipperItems(
      product?.displayShipperItems ?? []
    ),
    multipackItems: filterMultipackItems(product?.multipackItems ?? []),
  }));
};

const statusOrder = {
  success: 0,
  warning: 1,
  error: 2,
};

export const sortWizardProductByStatus = (products = []) => {
  if (!products || !products.length) return [];

  return [...products].sort((a, b) => {
    const aOrder = statusOrder[a.status?.toLowerCase()] ?? 99;
    const bOrder = statusOrder[b.status?.toLowerCase()] ?? 99;
    return aOrder - bOrder;
  });
};

export const totalCostWizardProducts = (wizardPaymentInfo = {}) => {
  const {
    fullCaptureCount = 0,
    unitCaptureCount = 0,
    productEachRate = 0,
    productHierarchyRate = 0,
  } = wizardPaymentInfo ?? {};

  const fullCaptureCost = fullCaptureCount * productHierarchyRate;
  const unitCaptureCost = unitCaptureCount * productEachRate;
  const totalCost = fullCaptureCost + unitCaptureCost;

  return {
    totalCost,
    isZeroCost: totalCost === 0,
  };
};

// Pack-size helpers — each covers one hierarchy shape.
// unitLink.quantity = units per innerpack (or case); innerPackLink.quantity = innerpacks per case.

const calcUnitCaseInnerPackSizes = (
  product,
  unitLink,
  _caseLink,
  innerPackLink
) => ({
  casePackSize: (innerPackLink.quantity ?? 1) * (unitLink.quantity ?? 1),
  caseInnerPackCount: innerPackLink.quantity ?? product.caseInnerPackCount,
  productInnerPackId: innerPackLink.productItemId || null,
  innerPackUpc: innerPackLink.upc12 || product.innerPackUpc,
  innerPackGtin: innerPackLink.gtin || product.innerPackGtin,
  innerPackPackSize: unitLink.quantity ?? product.innerPackPackSize,
});

const calcUnitCaseSizes = (product, unitLink, _caseLink) => ({
  casePackSize: unitLink.quantity ?? product.casePackSize,
  caseInnerPackCount: product.caseInnerPackCount,
  productInnerPackId: null,
  innerPackUpc: product.innerPackUpc,
  innerPackGtin: product.innerPackGtin,
  innerPackPackSize: product.innerPackPackSize,
});

const calcUnitInnerPackSizes = (product, unitLink, innerPackLink) => ({
  casePackSize: product.casePackSize,
  caseInnerPackCount: product.caseInnerPackCount,
  productInnerPackId: innerPackLink.productItemId || null,
  innerPackUpc: innerPackLink.upc12 || product.innerPackUpc,
  innerPackGtin: innerPackLink.gtin || product.innerPackGtin,
  innerPackPackSize: unitLink.quantity ?? product.innerPackPackSize,
});

export const applyHierarchyToProduct = (product, hierarchy) => {
  if (!hierarchy?.productHierarchyId) return product;

  const links = hierarchy.productItemLinks ?? [];

  const base = {
    ...product,
    hierarchyId: hierarchy.productHierarchyId,

    _preHierarchyUnitId: product._preHierarchyUnitId ?? product.productUnitId,
    _preHierarchyCaseId: product._preHierarchyCaseId ?? product.productCaseId,
    _preHierarchyInnerPackId:
      product._preHierarchyInnerPackId ?? product.productInnerPackId,
  };

  if (product.displayShipper) {
    return base;
  }

  const unitLink = links.find((l) => l.packageLevel === 'Unit');
  const caseLink = links.find((l) => l.packageLevel === 'Case');
  const innerPackLink = links.find((l) => l.packageLevel === 'Inner Pack');

  const packSizes =
    unitLink && caseLink && innerPackLink
      ? calcUnitCaseInnerPackSizes(product, unitLink, caseLink, innerPackLink)
      : unitLink && caseLink
        ? calcUnitCaseSizes(product, unitLink, caseLink)
        : unitLink && innerPackLink
          ? calcUnitInnerPackSizes(product, unitLink, innerPackLink)
          : {
              casePackSize: product.casePackSize,
              caseInnerPackCount: product.caseInnerPackCount,
              productInnerPackId: innerPackLink?.productItemId || null,
              innerPackUpc: innerPackLink?.upc12 || product.innerPackUpc,
              innerPackGtin: innerPackLink?.gtin || product.innerPackGtin,
              innerPackPackSize: product.innerPackPackSize,
            };

  return {
    ...base,
    ...unitLink,
    productUnitId: product?.productUnitId || unitLink?.productItemId || false,
    unitUpc: unitLink?.upc12 || product.unitUpc,

    productCaseId: caseLink?.productItemId || null,
    caseGtin: caseLink?.gtin || product.caseGtin,
    ...packSizes,
  };
};

export const hydrateProductsWithHierarchy = (products) =>
  products.map((product) => {
    if (!product.hierarchyId) return product;
    const matchedHierarchy = product.hierarchies?.find(
      (h) => h.productHierarchyId === product.hierarchyId
    );
    return matchedHierarchy
      ? applyHierarchyToProduct(product, matchedHierarchy)
      : product;
  });

// ---------------------------------------------------------------------------
// Display Shipper hierarchy helpers
// ---------------------------------------------------------------------------

/**
 * Returns the root Display Shipper item from a resolved hierarchy, or null
 * if the hierarchy is not a Display Shipper.
 *
 * @param {object} hierarchy - Resolved hierarchy (has `productItemLinks`).
 * @returns {object|null}
 */
export const getDisplayShipperRoot = (hierarchy) => {
  const links = hierarchy?.productItemLinks ?? [];
  return (
    links.find(
      (item) =>
        item.parentProductItemId === null &&
        item.packageLevel === 'Display Shipper'
    ) ?? null
  );
};

/**
 * Returns the direct Unit children of the Display Shipper root in a hierarchy.
 * Returns an empty array when the hierarchy is not a Display Shipper.
 *
 * @param {object} hierarchy - Resolved hierarchy (has `productItemLinks`).
 * @returns {{ productItemId: number, upc12: string, productName: string, productDescription: string, quantity: number }[]}
 */
export const getDisplayShipperUnitChildren = (hierarchy) => {
  const links = hierarchy?.productItemLinks ?? [];
  const root = getDisplayShipperRoot(hierarchy);
  if (!root) return [];

  return links.filter((item) => item.packageLevel === 'Unit');
};

/**
 * Maps hierarchy Unit children to the `displayShipperItems` shape used by the
 * Shipper Contents grid.
 *
 * @param {object[]} unitChildren - Items from `getDisplayShipperUnitChildren`.
 * @returns {{ productItemId: number|null, itemUpc: string, itemDescription: string, itemCount: number }[]}
 */
export const mapUnitChildrenToShipperItems = (unitChildren) =>
  unitChildren.map((child) => ({
    productItemId: child.productItemId ?? null,
    itemUpc: child.upc12 ?? '',
    itemDescription: child.productDescription || child.productName || '',
    itemCount: child.quantity ?? 1,
  }));

export const calculateDisplayShipperTotals = (displayShipperItems) => {
  const totalUnits = displayShipperItems.reduce(
    (sum, item) => sum + (item.itemCount || 0),
    0
  );

  return totalUnits;
};

// ---------------------------------------------------------------------------
// Multipack hierarchy helpers
// ---------------------------------------------------------------------------

/**
 * Sums `itemCount` across all multipack items.
 *
 * @param {object[]} multipackItems
 * @returns {number}
 */
export const calculateMultipackTotals = (multipackItems) =>
  multipackItems.reduce((sum, item) => sum + (item.itemCount || 0), 0);
